// ===========================================
// BUSTED! BLACKJACK - LOCKER PAGE
// ===========================================
// View owned cosmetics and equip them

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { api, LockerItem } from '../api/client';
import { formatBalance } from '../utils/format';
import bustedLogo from '../assets/busted.png';

// Category display names and icons
const CATEGORY_INFO: Record<string, { name: string; icon: string; description: string }> = {
  'table': { name: 'Table Felts', icon: '🎲', description: 'Customize your table surface' },
  'rail': { name: 'Table Rails', icon: '🪵', description: 'Change the table edge style' },
  'shoe': { name: 'Card Shoes', icon: '👟', description: 'Where cards are dealt from' },
  'tray': { name: 'Chip Trays', icon: '🗃️', description: 'Your personal chip holder' },
  'card-back': { name: 'Card Backs', icon: '🃏', description: 'Design on face-down cards' },
  'face-cards': { name: 'Face Cards', icon: '👑', description: 'Jack, Queen, King, and Ace designs' },
  'chip-set': { name: 'Chip Sets', icon: '🪙', description: 'Your betting chips' },
  'placemat': { name: 'Placemats', icon: '🎯', description: 'Your betting area' },
  'avatar-frame': { name: 'Avatar Frames', icon: '🖼️', description: 'Frame around your profile picture' },
};

// Order categories should appear
const CATEGORY_ORDER = [
  'table', 'rail', 'shoe', 'tray', 'card-back', 'face-cards', 'chip-set', 'placemat', 'avatar-frame'
];

export function LockerPage() {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuthStore();
  const [locker, setLocker] = useState<Record<string, LockerItem[]>>({});
  const [equipped, setEquipped] = useState<Record<string, string>>({});
  const [totalOwned, setTotalOwned] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [equippingId, setEquippingId] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(CATEGORY_ORDER));
  const [error, setError] = useState<string | null>(null);

  // Fetch locker on mount
  useEffect(() => {
    const fetchLocker = async () => {
      setIsLoading(true);
      const result = await api.cosmetics.getLocker();

      if (result.success && result.data) {
        setLocker(result.data.locker);
        setEquipped(result.data.equipped);
        setTotalOwned(result.data.totalOwned);
      } else {
        setError('Failed to load locker');
      }
      setIsLoading(false);
    };

    fetchLocker();
  }, []);

  const handleEquip = async (category: string, cosmeticId: string | null) => {
    if (equippingId) return;

    setEquippingId(cosmeticId || category);
    setError(null);

    const result = await api.cosmetics.equip(category, cosmeticId);

    if (result.success && result.data) {
      setEquipped(result.data.equipped);

      // Update local locker state to reflect equipped status
      setLocker(prev => {
        const updated = { ...prev };
        if (updated[category]) {
          updated[category] = updated[category].map(item => ({
            ...item,
            equipped: item.cosmeticId === cosmeticId,
          }));
        }
        return updated;
      });

      // Refresh user to update equipped cosmetics in global state
      fetchUser();
    } else {
      setError(result.error?.message || 'Failed to equip cosmetic');
    }

    setEquippingId(null);
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const getAcquiredLabel = (via: string) => {
    switch (via) {
      case 'purchase': return 'Purchased';
      case 'gift': return 'Gifted';
      case 'achievement': return 'Achievement';
      case 'default': return 'Default';
      case 'signup': return 'Welcome Gift';
      default: return via;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={bustedLogo} alt="BUSTED!" className="h-[50px]" />
              <div>
                <h1 className="text-xl font-bold text-white">My Locker</h1>
                <p className="text-sm text-gray-400">{totalOwned} cosmetic{totalOwned !== 1 ? 's' : ''} owned</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="px-4 py-2 bg-green-600 rounded-lg">
                <span className="text-white font-bold">{formatBalance(user?.balance ?? 0)}</span>
              </div>
              <button
                onClick={() => navigate('/store')}
                className="px-4 py-2 bg-casino-gold-500 hover:bg-casino-gold-400 text-gray-900 font-bold rounded-lg transition-colors"
              >
                Visit Store
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Home
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
            {error}
            <button onClick={() => setError(null)} className="ml-4 text-red-400 hover:text-red-300">
              Dismiss
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">Loading your locker...</div>
          </div>
        ) : totalOwned === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📦</div>
            <div className="text-gray-400 text-lg mb-2">Your locker is empty</div>
            <p className="text-gray-500 mb-6">Visit the store to get your first cosmetics!</p>
            <button
              onClick={() => navigate('/store')}
              className="px-6 py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-gray-900 font-bold rounded-xl transition-colors"
            >
              Browse Store
            </button>
          </div>
        ) : (
          /* Categories */
          <div className="space-y-4">
            {CATEGORY_ORDER.map(category => {
              const items = locker[category] || [];
              const info = CATEGORY_INFO[category];
              const isExpanded = expandedCategories.has(category);
              const equippedItem = items.find(item => item.equipped);

              return (
                <div key={category} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{info?.icon || '🎁'}</span>
                      <div className="text-left">
                        <h2 className="font-semibold text-white">{info?.name || category}</h2>
                        <p className="text-sm text-gray-400">{info?.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Currently Equipped */}
                      {equippedItem && (
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Equipped</p>
                          <p className="text-sm text-casino-gold-400">{equippedItem.name}</p>
                        </div>
                      )}

                      {/* Count */}
                      <div className="bg-gray-700 px-3 py-1 rounded-full text-sm text-gray-300">
                        {items.length}
                      </div>

                      {/* Expand Arrow */}
                      <span className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </button>

                  {/* Items Grid */}
                  {isExpanded && items.length > 0 && (
                    <div className="px-6 pb-6 pt-2">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {/* Default Option (Unequip) */}
                        <div
                          className={`bg-gray-700 border-2 rounded-xl p-4 cursor-pointer transition-colors ${
                            !equippedItem
                              ? 'border-casino-gold-500'
                              : 'border-transparent hover:border-gray-600'
                          }`}
                          onClick={() => handleEquip(category, null)}
                        >
                          <div className="aspect-square bg-gray-600 rounded-lg flex items-center justify-center mb-3">
                            <span className="text-3xl text-gray-400">🚫</span>
                          </div>
                          <p className="font-medium text-white text-sm text-center">Default</p>
                          <p className="text-xs text-gray-500 text-center mt-1">Use default style</p>
                          {!equippedItem && (
                            <div className="mt-2 text-center">
                              <span className="text-xs text-casino-gold-400">Active</span>
                            </div>
                          )}
                        </div>

                        {/* Owned Items */}
                        {items.map(item => (
                          <div
                            key={item.id}
                            className={`bg-gray-700 border-2 rounded-xl p-4 cursor-pointer transition-colors ${
                              item.equipped
                                ? 'border-casino-gold-500'
                                : 'border-transparent hover:border-gray-600'
                            }`}
                            onClick={() => handleEquip(category, item.cosmeticId)}
                          >
                            {/* Preview */}
                            <div className="aspect-square bg-gray-600 rounded-lg flex items-center justify-center mb-3 overflow-hidden relative">
                              {item.previewImageUrl ? (
                                <img
                                  src={item.previewImageUrl}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-3xl">{info?.icon || '🎁'}</span>
                              )}

                              {/* Loading overlay */}
                              {equippingId === item.cosmeticId && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <div className="w-6 h-6 border-2 border-casino-gold-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                            </div>

                            {/* Name */}
                            <p className="font-medium text-white text-sm text-center truncate">{item.name}</p>

                            {/* Acquired Via */}
                            <p className="text-xs text-gray-500 text-center mt-1">
                              {getAcquiredLabel(item.acquiredVia)}
                            </p>

                            {/* Equipped Badge */}
                            {item.equipped && (
                              <div className="mt-2 text-center">
                                <span className="text-xs text-casino-gold-400">Equipped</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty State for Category */}
                  {isExpanded && items.length === 0 && (
                    <div className="px-6 pb-6 pt-2 text-center text-gray-500">
                      <p>No {info?.name?.toLowerCase() || 'items'} in your locker</p>
                      <button
                        onClick={() => navigate('/store')}
                        className="mt-2 text-sm text-casino-gold-400 hover:text-casino-gold-300"
                      >
                        Browse store →
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default LockerPage;
